import { withQueryData } from "./withQueryData";
import { usePropertyId } from "./hooks/usePropertyId";
import { useWebsiteContentData } from "./hooks/useWebsiteContentData";

/**
 * HOC: withWebsiteContent
 *
 * Wraps a pure presentational component so that it fetches its own content
 * definition from the website-content API. The wrapped component only needs
 * to know about its visual props — all data is supplied automatically.
 *
 * Usage:
 *   const Hero = withWebsiteContent(HeroPure);
 *   // In JSX:
 *   <Hero id="hero" />
 *
 * How it works:
 *   1. Reads the current propertyId from Redux.
 *   2. Calls GET /api/properties/{propertyId}/website-content/{id}
 *   3. Spreads response.defaultProps as individual props into the wrapped component.
 *   4. Passes response.images as the `images` prop.
 *   5. Forwards `id` to the wrapped component for DOM anchors and hash links.
 *   6. Any additional props on the outer element are forwarded as overrides.
 *
 * Loading / error / 401 states are delegated to withQueryData.
 *
 * @param {React.ComponentType} Component  - Pure component that accepts content as props.
 * @returns {React.ComponentType}          - New component that accepts `id` and optional overrides.
 */
const withWebsiteContent = (Component) => {
    const displayName = Component.displayName || Component.name || 'Component';

    const Adapter = ({ data, ...propOverrides }) => {
        const { defaultProps = {}, images } = data;
        return <Component {...defaultProps} images={images ?? []} {...propOverrides} />;
    };

    const AdapterWithQueryData = withQueryData(Adapter);

    const WrappedComponent = ({ id, ...propOverrides }) => {
        const propertyId = usePropertyId();
        const { queryResults } = useWebsiteContentData(propertyId, id);
        return <AdapterWithQueryData queryData={queryResults} {...propOverrides} id={id} />;
    };

    WrappedComponent.displayName = `withWebsiteContent(${displayName})`;
    return WrappedComponent;
};

export default withWebsiteContent;
